import { useState } from 'react';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useNavigate, Link } from 'react-router-dom';

import './reset-password.css';

import { onError } from '../../../utils/error.util';
import { checkPasswordRequirement, validatePassword } from '../../../utils/password.util';

import Alert from '@mui/material/Alert';
import { BsArrowRepeat } from 'react-icons/bs';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  function validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function getErrorMessage(error: any): string {
    const errorMessage = error?.message || error?.toString() || '';
    const errorCode = error?.name || error?.code || '';

    // Handle specific Cognito error codes
    if (errorCode === 'UserNotFoundException' || errorMessage.includes('User does not exist')) {
      // Security: Don't reveal if user exists - use generic message
      return 'If an account exists with this email, a password reset code has been sent.';
    }
    if (errorCode === 'CodeMismatchException' || errorMessage.includes('Invalid verification code')) {
      return 'Invalid verification code. Please check your email and try again.';
    }
    if (errorCode === 'ExpiredCodeException' || errorMessage.includes('Code has expired')) {
      return 'The verification code has expired. Please request a new code.';
    }
    if (errorCode === 'LimitExceededException' || errorMessage.includes('Too many attempts')) {
      return 'Too many attempts. Please try again later.';
    }
    if (errorCode === 'InvalidPasswordException' || errorMessage.includes('Password did not conform')) {
      return 'Password does not meet requirements. Please check the password requirements below.';
    }
    if (errorMessage.includes('Network') || errorMessage.includes('Failed to fetch')) {
      return 'Network error. Please check your internet connection and try again.';
    }

    // Return the error message if it's user-friendly, otherwise return a generic message
    if (errorMessage && !errorMessage.includes('AWS') && !errorMessage.includes('Cognito')) {
      return errorMessage;
    }

    return 'An error occurred. Please try again.';
  }


  async function handleRequestReset(event: any) {
    event.preventDefault();

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validate email format
    if (!validateEmail(email)) {
      setErrorMessage('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      await resetPassword({ username: email });
      // Security: Always show success message, don't reveal if user exists
      setSuccessMessage('If an account exists with this email, a password reset code has been sent.');
      setStep('confirm');
    } catch (e: any) {
      onError(e);
      // For UserNotFoundException, show generic success message for security
      if (e?.name === 'UserNotFoundException') {
        setSuccessMessage('If an account exists with this email, a password reset code has been sent.');
        setStep('confirm');
      } else {
        setErrorMessage(getErrorMessage(e));
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleConfirmReset(event: any) {
    event.preventDefault();

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password requirements
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setErrorMessage(passwordError);
      setIsLoading(false);
      return;
    }

    // Validate confirmation code
    if (!confirmationCode || confirmationCode.length === 0) {
      setErrorMessage('Please enter the verification code');
      setIsLoading(false);
      return;
    }

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: confirmationCode,
        newPassword: newPassword,
      });
      setSuccessMessage('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (e: any) {
      onError(e);
      setErrorMessage(getErrorMessage(e));
      setIsLoading(false);
    }
  }

  function handleResendCode() {
    setStep('request');
    setConfirmationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setErrorMessage('');
    setSuccessMessage('');
  }

  return (
    <div id="login-section">
      <div id="bg"></div>
      <section id="login">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 mx-auto my-auto">
              <img alt="CCL" id="ccl-logo" src="/assets/images/collector-car-leaders-logo.png" />
            </div>
            <div className="col-12">
              {step === 'request' ? (
                <form onSubmit={handleRequestReset}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', textAlign: 'center' }}>
                    Reset Password
                  </Typography>
                  {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {errorMessage}
                    </Alert>
                  )}
                  {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {successMessage}
                    </Alert>
                  )}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Enter your email address and we'll send you a verification code to reset your password.
                  </Alert>
                  <TextField
                    fullWidth
                    required
                    id="email"
                    name="email"
                    type="email"
                    label="Email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    sx={{ mb: 2 }}
                  />
                  <button id="submit-btn" type="submit" disabled={!email || !validateEmail(email) || isLoading}>
                    {isLoading && <BsArrowRepeat className="spinning" />}
                    Send Reset Code
                  </button>
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>
                      Back to Login
                    </Link>
                  </Box>
                </form>
              ) : (
                <form onSubmit={handleConfirmReset}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', textAlign: 'center' }}>
                    Enter Verification Code
                  </Typography>
                  {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {errorMessage}
                    </Alert>
                  )}
                  {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {successMessage}
                    </Alert>
                  )}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please check your email for the verification code and enter it below along with your new password.
                  </Alert>
                  <TextField
                    fullWidth
                    required
                    id="confirmationCode"
                    name="confirmationCode"
                    type="text"
                    label="Verification Code"
                    autoComplete="one-time-code"
                    autoFocus
                    value={confirmationCode}
                    onChange={(e) => {
                      setConfirmationCode(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    required
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    label="New Password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                              aria-label="toggle password visibility"
                            >
                              {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ mb: 2 }}
                  />
                  {newPassword && (
                    <Box sx={{ mb: 2, mt: 1 }}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                        Password requirements:
                      </Typography>
                      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {checkPasswordRequirement(newPassword, 'length') ? (
                            <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                          )}
                          <span>At least 8 characters</span>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {checkPasswordRequirement(newPassword, 'uppercase') ? (
                            <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                          )}
                          <span>At least one uppercase letter</span>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {checkPasswordRequirement(newPassword, 'lowercase') ? (
                            <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                          )}
                          <span>At least one lowercase letter</span>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {checkPasswordRequirement(newPassword, 'number') ? (
                            <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                          )}
                          <span>At least one number</span>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {checkPasswordRequirement(newPassword, 'special') ? (
                            <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                          )}
                          <span>At least one special character</span>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    required
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm Password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              aria-label="toggle password visibility"
                            >
                              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ mb: 2 }}
                  />
                  {confirmPassword && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      {newPassword === confirmPassword ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: 'success.main' }}>
                          <CheckCircleIcon sx={{ fontSize: '14px', mr: 0.5 }} />
                          <span>Passwords match</span>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: 'error.main' }}>
                          <CancelIcon sx={{ fontSize: '14px', mr: 0.5 }} />
                          <span>Passwords do not match</span>
                        </Box>
                      )}
                    </Box>
                  )}
                  <button
                    id="submit-btn"
                    type="submit"
                    disabled={!confirmationCode || !newPassword || !confirmPassword || isLoading}
                  >
                    {isLoading && <BsArrowRepeat className="spinning" />}
                    Reset Password
                  </button>
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                      onClick={handleResendCode}
                    >
                      Resend Code
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>
                        Back to Login
                      </Link>
                    </Box>
                  </Box>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
